import { useEffect, useState } from "react";
import { ImageIcon, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { ImageField } from "@/components/builder/ImageField";

type Slot = { label: string; file_id: string | null; url: string | null };
const HELP: Record<string, string> = { cover: "Used on the cover when a quote has no cover photo or mockup yet.", before_after: "Used on the Design Preview page when there is no before photo or AI mockup.", content: "Used on content pages that have an image layout but no picture of their own.", components: "Used for component cards that have no picture." };

export default function SettingsImages() {
  const { user } = useAuth() as any;
  const admin = user?.role === "admin";
  const [slots, setSlots] = useState<Record<string, Slot>>({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings/fallback-images").then((r) => setSlots(r.data.slots)).catch((e) => toast.error(errMsg(e))); }, []);
  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/settings/fallback-images", { slots: Object.fromEntries(Object.entries(slots).map(([k, v]) => [k, { file_id: v.file_id }])) }); setSlots(r.data.slots); setDirty(false); toast.success("Backup pictures saved"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <AppShell title="Settings · Backup pictures" actions={admin && <Button size="sm" className="bg-blue-700 hover:bg-blue-800" data-testid="fallback-save-btn" disabled={!dirty || busy} onClick={save}><Save size={14} className="mr-1.5" />{busy ? "Saving…" : "Save"}</Button>}>
      <p className="mb-4 text-sm text-gray-500">These pictures fill in automatically wherever a quote page has no picture of its own — on screen, in the public proposal link and in the PDF. Upload your own or pick from the image library; page pictures set on an individual quote always win.</p>
      <div className="grid gap-4 md:grid-cols-2" data-testid="fallback-slots">
        {Object.entries(slots).map(([key, s]) => (
          <div key={key} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm" data-testid={`fallback-slot-${key}`}>
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-900"><ImageIcon size={15} className="text-blue-700" />{s.label}</div>
            <p className="mt-0.5 text-xs text-gray-500">{HELP[key]}</p>
            {!s.url && <div className="mt-2 flex h-28 items-center justify-center rounded border border-dashed border-gray-300 text-xs text-gray-400" data-testid={`fallback-slot-${key}-empty`}>No backup picture — a generic stock photo is used</div>}
            <ImageField testid={`fallback-slot-${key}-image`} kind="page-images" showUrl={false} disabled={!admin} url={s.url} fileId={s.file_id} libraryName={`Backup · ${s.label}`}
              onChange={(url, fid) => { setSlots({ ...slots, [key]: { ...s, url: url && fid ? url : null, file_id: fid } }); setDirty(true); if (url && !fid) toast.error("Please upload a picture or choose one from the library"); }} />
          </div>
        ))}
      </div>
    </AppShell>
  );
}
